import { useEffect, useState } from "react";
import { ApiError, type ImApi } from "../domain/api";
import {
  contentLabel,
  isAudioPayload,
  isImagePayload,
  isJsonObject,
  isMediaReferencePayload,
  parseJsonPayload,
  type AudioPayload,
  type ImagePayload,
  type MediaReferencePayload,
  type MaterializedMessage,
} from "../domain/messages";
import type { ConversationMember } from "../domain/types";
import { Avatar } from "./Avatar";

export function MessageBubble({
  message,
  currentUserId,
  members,
  api,
  onEdit,
  onRetract,
}: {
  readonly message: MaterializedMessage;
  readonly currentUserId: string;
  readonly members: readonly ConversationMember[];
  readonly api: ImApi;
  readonly onEdit: (message: MaterializedMessage) => void;
  readonly onRetract: (message: MaterializedMessage) => void;
}) {
  const self = message.senderId === currentUserId;
  const sender = members.find((member) => member.userId === message.senderId);
  const senderName = message.systemAccount?.displayName ?? sender?.displayName ?? message.senderId;
  const senderAvatar = message.systemAccount?.avatarUrl ?? sender?.avatarUrl;
  if (message.contentType === "system") {
    return <div className="system-message"><span>{message.payload}</span></div>;
  }
  return (
    <article className={`message-row ${self ? "self" : "other"}`} data-message-id={message.messageId}>
      {self ? null : <Avatar name={senderName} url={senderAvatar} size={36} />}
      <div className="message-stack">
        {self ? null : <small className="sender-name">{senderName}{message.senderType === "SYSTEM_ACCOUNT" ? <span className="system-account-badge">系统</span> : null}</small>}
        <div className={`message-bubble type-${message.contentType}`}>
          <MessageContent message={message} api={api} />
          {message.edited ? <small className="edited-label">已编辑</small> : null}
        </div>
        <div className="message-meta">
          <time>{formatTime(message.createdAt)}</time>
          {self ? <span className="sent-state" title="服务器已确认">✓ 已发送</span> : null}
          {self && !message.retracted ? (
            <span className="message-actions">
              {message.contentType === "text" ? <button type="button" onClick={() => { onEdit(message); }}>编辑</button> : null}
              <button type="button" onClick={() => { onRetract(message); }}>撤回</button>
            </span>
          ) : null}
        </div>
      </div>
      {self ? <Avatar name={senderName} url={senderAvatar} size={36} /> : null}
    </article>
  );
}

function MessageContent({ message, api }: { readonly message: MaterializedMessage; readonly api: ImApi }) {
  if (message.retracted) return <span className="retracted-copy">消息已撤回</span>;
  if (message.contentType === "text" || message.contentType === "edit") {
    return <p className="text-content">{message.payload}</p>;
  }
  if (message.contentType === "image") {
    const payload = parseJsonPayload<ImagePayload>(message.payload, isImagePayload);
    return payload === null ? <InvalidPayload type="image" /> : (
      <ProtectedImage payload={payload} api={api} />
    );
  }
  if (message.contentType === "audio") {
    const payload = parseJsonPayload<AudioPayload>(message.payload, isAudioPayload);
    return payload === null ? <InvalidPayload type="audio" /> : (
      <ProtectedAudio payload={payload} api={api} />
    );
  }
  if (message.contentType === "media-ref") {
    const payload = parseJsonPayload<MediaReferencePayload>(
      message.payload,
      isMediaReferencePayload,
    );
    return payload === null ? <InvalidPayload type="media-ref" /> : (
      <ProtectedAttachment payload={payload} api={api} />
    );
  }
  if (message.contentType === "custom") {
    const payload = parseJsonPayload(message.payload, isJsonObject);
    const description = typeof payload?.description === "string" ? payload.description : "自定义消息";
    return <div className="structured-message"><b>{description}</b>
      <pre>{JSON.stringify(payload?.data ?? payload, null, 2)}</pre></div>;
  }
  if (message.contentType === "command") {
    const payload = parseJsonPayload(message.payload, isJsonObject);
    const name = typeof payload?.name === "string" ? payload.name : "unknown";
    const version = typeof payload?.version === "number" ? payload.version : 1;
    return <div className="command-message"><span>CMD</span><b>{name}</b>
      <small>v{version}{payload?.requireAck === true ? " · 需要 ACK" : ""}</small>
      {payload?.parameters === undefined ? null : <pre>{JSON.stringify(payload.parameters, null, 2)}</pre>}</div>;
  }
  return <div className="opaque-message"><b>{contentLabel(message.contentType)}</b><code>{message.payload || "（空载荷）"}</code></div>;
}

function ProtectedImage({ payload, api }: { readonly payload: ImagePayload; readonly api: ImApi }) {
  const source = payload.original;
  const media = useProtectedUrl(source.objectKey, source.url, api);
  if (media.status === "expired") return <ExpiredMedia />;
  if (media.status === "failed") return <FailedMedia />;
  return media.url === null ? <div className="media-loading"><span className="spinner" /> 图片加载中</div> : (
    <img className="message-image" src={media.url} width={source.width} height={source.height} alt="聊天图片" />
  );
}

function ProtectedAudio({ payload, api }: { readonly payload: AudioPayload; readonly api: ImApi }) {
  const media = useProtectedUrl(payload.source.objectKey, payload.source.url, api);
  if (media.status === "expired") return <ExpiredMedia />;
  if (media.status === "failed") return <FailedMedia />;
  return (
    <div className="audio-message">
      <span className="audio-wave">{waveBars(payload.waveform)}</span>
      {media.url === null ? <span>语音加载中…</span> : <audio controls preload="metadata" src={media.url} />}
      <small>{Math.max(1, Math.round(payload.durationMs / 1_000))}″</small>
    </div>
  );
}

function ProtectedAttachment({ payload, api }: {
  readonly payload: MediaReferencePayload;
  readonly api: ImApi;
}) {
  const media = useProtectedUrl(payload.source.objectKey, payload.source.url, api);
  if (media.status === "expired") return <ExpiredMedia />;
  if (media.status === "failed") return <FailedMedia />;
  if (payload.kind === "video") {
    return media.url === null ? <div className="media-loading"><span className="spinner" /> 视频加载中</div> : (
      <video className="message-video" controls preload="metadata" src={media.url} />
    );
  }
  return (
    <a className="file-message" href={media.url ?? undefined} download={payload.fileName}
      aria-disabled={media.url === null}>
      <span>FILE</span><b>{payload.fileName}</b><small>{formatBytes(payload.source.sizeBytes)}</small>
    </a>
  );
}

function useProtectedUrl(
  objectKey: string | undefined,
  externalUrl: string | undefined,
  api: ImApi,
): { readonly url: string | null; readonly status: "loading" | "ready" | "expired" | "failed" } {
  const [url, setUrl] = useState<string | null>(externalUrl ?? null);
  const [status, setStatus] = useState<"loading" | "ready" | "expired" | "failed">(
    externalUrl === undefined ? "loading" : "ready",
  );
  useEffect(() => {
    let active = true;
    if (externalUrl !== undefined) {
      setUrl(externalUrl);
      setStatus("ready");
    }
    else if (objectKey !== undefined) {
      setStatus("loading");
      void api.mediaUrl(objectKey).then((value) => {
        if (!active) return;
        setUrl(value);
        setStatus("ready");
      }).catch((error: unknown) => {
        if (!active) return;
        setStatus(error instanceof ApiError && error.code === "MEDIA_EXPIRED"
          ? "expired"
          : "failed");
      });
    } else {
      setStatus("failed");
    }
    return () => { active = false; };
  }, [api, externalUrl, objectKey]);
  return { url, status };
}

function ExpiredMedia() {
  return <div className="opaque-message"><b>媒体已过期</b><code>该媒体文件已超过保留期</code></div>;
}

function FailedMedia() {
  return <div className="opaque-message"><b>媒体加载失败</b><code>请稍后重试</code></div>;
}

function InvalidPayload({ type }: { readonly type: string }) {
  return <div className="opaque-message"><b>{type}</b><code>消息载荷无法解析</code></div>;
}

function waveBars(waveform: readonly number[] | undefined): string {
  const bars = waveform?.slice(0, 20) ?? [40, 75, 50, 95, 65, 35, 80, 55, 90, 45, 70];
  return bars.map((value) => value > 70 ? "▮" : value > 40 ? "▯" : "·").join("");
}

function formatBytes(value: number): string {
  if (value < 1_024) return `${value} B`;
  if (value < 1_024 * 1_024) return `${(value / 1_024).toFixed(1)} KB`;
  return `${(value / (1_024 * 1_024)).toFixed(1)} MB`;
}

function formatTime(timestamp: number): string {
  return new Intl.DateTimeFormat("zh-CN", { hour: "2-digit", minute: "2-digit" }).format(timestamp);
}
